export const isEmptyValue = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0);

const sqlLikePattern =
  /(\bselect\b[\s\S]*\bfrom\b|\binsert\b\s+\binto\b|\bupdate\b[\s\S]*\bset\b|\bdelete\b\s+\bfrom\b|\bdrop\b\s+\b(table|database)\b|\btruncate\b\s+\btable\b|\bunion\b\s+\bselect\b|--|\/\*|\*\/|\bor\b\s+['"]?\w+['"]?\s*=\s*['"]?\w+|\band\b\s+['"]?\w+['"]?\s*=\s*['"]?\w+)/i;

const repeatedSpecialCharacterPattern = /([!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])\1{3,}/;

const specialCharacterSequencePattern = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,}/;

export const globalValidationRules = [
  {
    validate: (value) => {
      if (typeof value !== "string" || isEmptyValue(value)) return true;
      return !sqlLikePattern.test(value) || "Suspicious input is not allowed";
    },
  },
  {
    validate: (value) => {
      if (typeof value !== "string" || isEmptyValue(value)) return true;
      return (
        !repeatedSpecialCharacterPattern.test(value) ||
        "Repeated special characters are not allowed"
      );
    },
  },
  {
    validate: (value) => {
      if (typeof value !== "string" || isEmptyValue(value)) return true;
      return (
        !specialCharacterSequencePattern.test(value) ||
        "Too many special characters are not allowed"
      );
    },
  },
];

const normalizeRules = (rules) => {
  if (!rules) return [];
  return Array.isArray(rules) ? rules : [rules];
};

export const validateFieldValue = (
  fieldName,
  value,
  fieldRules = [],
  formData = {},
  options = {},
) => {
  const rules = [
    ...(options.skipGlobalValidation ? [] : globalValidationRules),
    ...normalizeRules(fieldRules),
  ];

  for (const rule of rules) {
    if (typeof rule === "function") {
      const result = rule(value, formData, fieldName);
      if (result !== true && result) return result;
      continue;
    }

    if (!rule) continue;

    if (rule.required && isEmptyValue(value)) {
      return rule.message || "This field is required";
    }

    if (isEmptyValue(value) && !rule.required) {
      continue;
    }

    if (rule.minLength && String(value).trim().length < rule.minLength) {
      return rule.message || `Minimum ${rule.minLength} characters required`;
    }

    if (rule.maxLength && String(value).trim().length > rule.maxLength) {
      return rule.message || `Maximum ${rule.maxLength} characters allowed`;
    }

    if (rule.min !== undefined && Number(value) < rule.min) {
      return rule.message || `Value must be at least ${rule.min}`;
    }

    if (rule.max !== undefined && Number(value) > rule.max) {
      return rule.message || `Value must be at most ${rule.max}`;
    }

    if (rule.pattern && !rule.pattern.test(String(value))) {
      return rule.message || "Invalid value";
    }

    if (rule.validate) {
      const result = rule.validate(value, formData, fieldName);
      if (result !== true && result) return result;
    }
  }

  return "";
};

export const validateFormValues = (
  formData,
  validationRules = {},
  options = {},
) => {
  const errors = {};
  const fieldNames = Array.from(
    new Set([...Object.keys(formData), ...Object.keys(validationRules)]),
  );

  fieldNames.forEach((fieldName) => {
    const error = validateFieldValue(
      fieldName,
      formData[fieldName],
      validationRules[fieldName],
      formData,
      options,
    );

    if (error) {
      errors[fieldName] = error;
    }
  });

  return errors;
};
